<script>
// based on: https://codepen.io/team/wowza/pen/GRZrVrQ
// see also: https://github.com/WowzaMediaSystems/webrtc-examples
import {tick} from 'svelte'
import {env} from '@lib'
import {sdpUrl, sdpApp, sdpStream} from '@store/query'

let open = false
let video
let wsConnection, peerConnection

async function start() {
	open = true

	await tick() // Wait for the video element to be created

	connect()
}

function connect() {
	if (!video)
		return console.error('Can not start. Video element not present.')

	const streamInfo = {
		applicationName: $sdpApp,
		streamName: $sdpStream,
	}
	const userData = { iceServers: [] }

	let repeaterRetryCount

	try {
		wsConnection = new WebSocket($sdpUrl)
		peerConnection = new RTCPeerConnection(userData)
	}
	catch (e) {
		console.log(e)
		return
	}

	wsConnection.binaryType = 'arraybuffer'

	wsConnection.addEventListener('open', () => {
		console.log('ws open')

		peerConnection.addEventListener('track', () => {
			console.log('gotRemoteTrack: kind:' + event.track.kind + ' stream:' + event.streams[0])

			try {
				video.srcObject = event.streams[0]
			} catch (error) {
				video.src = window.URL.createObjectURL(event.streams[0])
			}
		})

		sendPlayGetOffer()
	})

	function sendPlayGetOffer() {
		console.log('sendPlayGetOffer: ' + JSON.stringify(streamInfo))

		wsConnection.send(JSON.stringify({
			direction: 'play',
			command: 'getOffer',
			streamInfo,
			userData,
		}))
	}

	wsConnection.addEventListener('message', (evt) => {
		console.log('wsConnection.onmessage: ' + evt.data)

		const msgJSON = JSON.parse(evt.data)
		const msgStatus = Number(msgJSON['status'])
		const msgCommand = msgJSON['command']

		if (514 == msgStatus) { // repeater stream not ready
			repeaterRetryCount++

			if (repeaterRetryCount < 10) {
				setTimeout(sendPlayGetOffer, 500)
			} else {
				console.log('Live stream repeater timeout: ' + $sdpStream)
				stop()
			}
		} else if (200 != msgStatus) {
			console.log(msgJSON['statusDescription'])
			stop()
		} else {
			const streamInfoResponse = msgJSON['streamInfo']
			if (streamInfoResponse !== undefined)
				streamInfo.sessionId = streamInfoResponse.sessionId

			const sdpData = msgJSON['sdp']
			if (sdpData != null) {
				console.log('sdp: ' + JSON.stringify(msgJSON['sdp']))

				if (mungeSDP != null)
					msgJSON.sdp.sdp = mungeSDP(msgJSON.sdp.sdp)

				// Enhance here if Safari is a published stream.
				console.log('SDP Data: ' + msgJSON.sdp.sdp)

				peerConnection
					.setRemoteDescription(new RTCSessionDescription(msgJSON.sdp))
					.then(async () => {
						const description = await peerConnection.createAnswer()

						try {
							await peerConnection.setLocalDescription(description)

							console.log('sendAnswer')

							wsConnection.send(JSON.stringify({
								direction: 'play',
								command: 'sendResponse',
								streamInfo,
								sdp: description,
								userData,
							}))
						} catch (err) {
							console.log('set local description error', err)
						}
					}).catch(err => console.log('set remote description error', err))
			}

			const iceCandidates = msgJSON['iceCandidates']
			if (iceCandidates != null)
				for (const index in iceCandidates) {
					console.log('iceCandidates: ' + JSON.stringify(iceCandidates[index]))
					peerConnection.addIceCandidate(new RTCIceCandidate(iceCandidates[index]))
				}
		}

		if ('sendResponse'.localeCompare(msgCommand) == 0) {
			if (wsConnection != null)
				wsConnection.close()

			wsConnection = null
		}
	})

	wsConnection.addEventListener('close', () => {
		console.log('wsConnection close')
	})

	wsConnection.addEventListener('error', console.log)
}

function mungeSDP(sdpStr) {
	// For greatest playback compatibility,
	// force H.264 playback to baseline (42e01f).
	const sdpLines = sdpStr.split(/\r\n/)
	let sdpStrRet = ''

	for (const sdpIndex in sdpLines) {
		let sdpLine = sdpLines[sdpIndex]

		if (sdpLine.length == 0)
			continue

		if (sdpLine.includes('profile-level-id')) {
			// The profile-level-id string has three parts: XXYYZZ, where
			//   XX: 42 baseline, 4D main, 64 high
			//   YY: constraint
			//   ZZ: level ID
			// Look for codecs higher than baseline and force downward.
			const profileLevelId = sdpLine.substr(sdpLine.indexOf('profile-level-id')+17,6)
			let profile = Number('0x'+profileLevelId.substr(0,2))
			let constraint = Number('0x'+profileLevelId.substr(2,2))
			let level = Number('0x'+profileLevelId.substr(4,2))

			if (profile > 0x42) {
				profile = 0x42
				constraint = 0xE0
				level = 0x1F
			}

			let newProfileLevelId = ('00' + profile.toString(16)).slice(-2).toLowerCase() +
				('00' + constraint.toString(16)).slice(-2).toLowerCase() +
				('00' + level.toString(16)).slice(-2).toLowerCase()

			sdpLine = sdpLine.replace(profileLevelId,newProfileLevelId)
		}

		sdpStrRet += sdpLine
		sdpStrRet += '\r\n'
	}

	return sdpStrRet
}

function stop() {
	if (peerConnection != null)
		peerConnection.close()

	if (wsConnection != null)
		wsConnection.close()

	peerConnection = null
	wsConnection = null

	video.src = ''
	open = false
}
</script>

{#if open}
	<video
			bind:this={video}
			autoplay
			playsinline
			muted
			controls >
	</video>
	<button on:click={stop}>
		Disconnect
	</button>
{:else}
	<form on:submit|preventDefault={start}>
		<input
			required
			placeholder="Signaling URL"
			name=sdpUrl
			bind:value={$sdpUrl} >
		<input
			required
			placeholder="Application name"
			name=applicationName
			bind:value={$sdpApp} >
		<input
			required
			placeholder="Stream name"
			name=streamName
			bind:value={$sdpStream} >

		<button>
				Connect
		</button>
	</form>
{/if}

<style>
	video {
		width: 100%;
	}

	input {
		width: 100%;
		margin-bottom: var(--spacing-sm);
	}
</style>
